:host {
  all: initial;
}

/* Host page CSS may still hide the root (display), but must not create a containing block for the fixed UI. */
:host {
  position: static !important;
  transform: none !important;
  filter: none !important;
  backdrop-filter: none !important;
  perspective: none !important;
  contain: none !important;
  will-change: auto !important;
}

*,
*::before,
*::after {
  box-sizing: border-box;
}

.launcher {
  position: fixed;
  z-index: var(--pulxon-z, 2147483000);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* One scale variable drives both the launcher and the panel (see `.panel` below) — set once, on
     the mount point, from `settings.ui.scale`; there is no second, hard-coded "large" size set. */
  width: calc(var(--pulxon-size, 52px) * var(--pulxon-scale, 1));
  height: calc(var(--pulxon-size, 52px) * var(--pulxon-scale, 1));
  padding: 0;
  border: 2px solid #ffffff;
  border-radius: 50%;
  background: var(--pulxon-accent, #1f4bff);
  color: var(--pulxon-on-accent, #ffffff);
  cursor: pointer;
  box-shadow: 0 2px 8px rgb(0 0 0 / 0.35);
}

/* Inverted page contrast sets --pulxon-f-undo on <html>; re-invert the UI so it keeps its real colors. */
.launcher,
.panel {
  filter: var(--pulxon-f-undo, none);
}

.launcher svg {
  width: 60%;
  height: 60%;
  fill: currentColor;
}

.launcher--small { --pulxon-size: 44px; }
.launcher--medium { --pulxon-size: 52px; }
.launcher--large { --pulxon-size: 64px; }

.launcher--top-left { top: var(--pulxon-oy); left: var(--pulxon-ox); }
.launcher--top-center { top: var(--pulxon-oy); left: 50%; transform: translateX(-50%); }
.launcher--top-right { top: var(--pulxon-oy); right: var(--pulxon-ox); }
.launcher--center-left { top: 50%; left: var(--pulxon-ox); transform: translateY(-50%); }
.launcher--center-right { top: 50%; right: var(--pulxon-ox); transform: translateY(-50%); }
.launcher--bottom-left { bottom: var(--pulxon-oy); left: var(--pulxon-ox); }
.launcher--bottom-center { bottom: var(--pulxon-oy); left: 50%; transform: translateX(-50%); }
.launcher--bottom-right { bottom: var(--pulxon-oy); right: var(--pulxon-ox); }

/* Dragged somewhere by the visitor: `left`/`top` come inline from Launcher.tsx. */
.launcher {
  /* A finger on the launcher drags it instead of scrolling the page underneath. */
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
}
.launcher--free { right: auto; bottom: auto; transform: none; }
.launcher--dragging { cursor: grabbing; box-shadow: 0 6px 18px rgb(0 0 0 / 0.4); }

:focus-visible {
  outline: 3px solid #111111;
  outline-offset: 2px;
  box-shadow: 0 0 0 6px #ffffff;
}

.panel {
  position: fixed;
  z-index: var(--pulxon-z, 2147483000);
  display: flex;
  flex-direction: column;
  width: min(calc(340px * var(--pulxon-scale, 1)), 100vw);
  overflow: hidden;
  background: #f1f3f7;
  color: #111111;
  /* Same `--pulxon-scale` the launcher reads. Text throughout the panel is sized in `em`/`%` against
     this base, so it grows with it — and so do the panel's own controls: a visitor who asks for a
     larger widget wants those bigger too, not just the text next to them. */
  font: calc(16px * var(--pulxon-scale, 1)) / 1.5 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
  box-shadow: 0 8px 32px rgb(0 0 0 / 0.3);
}

/* Docked: the full height of the screen, on the launcher's side, as Sienna's panel opens. */
.panel--docked {
  top: 0;
  bottom: 0;
}
.panel--left { left: 0; }
.panel--right { right: 0; }

/* Floating: dragged off the edge by its title bar. `left`/`top` come inline from Panel.tsx. */
.panel--floating {
  max-height: min(calc(640px * var(--pulxon-scale, 1)), calc(100vh - 16px));
  max-height: min(calc(640px * var(--pulxon-scale, 1)), calc(100dvh - 16px));
  width: min(calc(340px * var(--pulxon-scale, 1)), calc(100vw - 16px));
  border-radius: 16px;
}

/* The drag handle: the whole title bar except its buttons and link. */
.panel__header {
  flex: none;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 10px 10px 12px;
  color: var(--pulxon-on-accent, #ffffff);
  background: var(--pulxon-accent, #1f4bff);
  cursor: grab;
  /* A finger on the title bar moves the panel instead of scrolling the page. */
  touch-action: none;
  -webkit-user-select: none;
  user-select: none;
}

.panel__header:active { cursor: grabbing; }

.panel__grip {
  flex: none;
  width: 10px;
  height: 16px;
  fill: currentColor;
  opacity: 0.8;
}

.panel__heading {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

/* The signature, right under the title. Underlined, not colour alone, and in the header's own
   foreground, which mount.tsx picks for contrast against whatever accent the site chose. The
   padding brings it to the 24px WCAG 2.5.8 target. */
.panel__brand {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  margin: -2px -4px -4px;
  padding: 0 4px;
  font-size: 0.75em;
  font-weight: 600;
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;
  border-radius: 4px;
  cursor: pointer;
}

.panel__brand:hover { text-decoration-thickness: 2px; }

/* The one scrolling region. `contain` keeps a scroll that reaches the end from carrying on into the
   page behind, which reads as the panel "not scrolling" once the page starts moving instead. */
.panel__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 12px;
  scrollbar-width: thin;
  scrollbar-color: #9aa3b8 transparent;
}

h2 { margin: 0; font-size: 1.0625em; }

/* A white card per section, on the panel's grey. */
.card {
  margin-bottom: 12px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #e3e6ee;
  border-radius: 12px;
}

.card:last-child { margin-bottom: 0; }

.card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.card__title {
  margin: 0;
  font-size: 0.75em;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #1d2433;
}

.info-button {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  color: #4b5563;
  background: transparent;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
}

.info-button[aria-expanded="true"] {
  color: var(--pulxon-on-accent, #ffffff);
  background: var(--pulxon-accent, #1f4bff);
}

.card__info {
  margin: -4px 0 10px;
  font-size: 0.8125em;
  color: #374151;
}

.card__info[hidden] { display: none; }

.ui-icon {
  flex: none;
  width: 1.125em;
  height: 1.125em;
}

/* Language and size, and position and reset. */
.language-select {
  width: 100%;
  min-height: 40px;
  padding: 0 10px;
  font: inherit;
  font-size: 0.875em;
  color: #111111;
  background: #ffffff;
  border: 1px solid #c5cad6;
  border-radius: 10px;
}

.settings-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  margin-top: 10px;
  font-size: 0.875em;
}

.settings-row:first-child { margin-top: 0; }

.settings-row > span {
  font-weight: 600;
}

/* Two or three buttons sharing one pill. */
.segmented {
  display: inline-flex;
  border: 2px solid #e2e6f0;
  border-radius: 999px;
  overflow: hidden;
}

.segmented button {
  /* 2.5em of the row's 14px (0.875em) text is 35px, comfortably over the 24px WCAG 2.5.8 target
     minimum, and grows from there with --pulxon-scale. */
  min-width: 2.5em;
  min-height: 2.5em;
  padding: 0.375em 0.75em;
  font: inherit;
  font-weight: 600;
  color: #111111;
  background: #ffffff;
  border: 0;
  cursor: pointer;
}

.segmented button + button {
  border-inline-start: 2px solid #e2e6f0;
}

/* Border, not just fill, changes too: state is never colour alone. */
.segmented button[aria-pressed="true"] {
  color: var(--pulxon-on-accent, #ffffff);
  background: var(--pulxon-accent, #1f4bff);
}

.icon-button {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  font-size: 22px;
  line-height: 1;
  color: inherit;
  background: transparent;
  border: 2px solid currentColor;
  border-radius: 50%;
  cursor: pointer;
}

/* Profiles: two cards a row, each a switch. */
.profile-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.profile-card {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 4px;
  min-width: 0;
  padding: 10px;
  font: inherit;
  text-align: start;
  color: #111111;
  background: #ffffff;
  border: 1px solid #e3e6ee;
  border-radius: 10px;
  cursor: pointer;
}

.profile-card[aria-checked="true"] {
  border: 2px solid var(--pulxon-accent, #1f4bff);
  padding: 9px;
  background: #f5f7ff;
}

.profile-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 4px;
}

.profile-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: var(--pulxon-accent, #1f4bff);
  background: #eef2ff;
  border-radius: 8px;
}

.profile-card__name {
  font-size: 0.8125em;
  font-weight: 700;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

.profile-card__desc {
  font-size: 0.6875em;
  line-height: 1.35;
  color: #4b5563;
  overflow-wrap: anywhere;
}

/* The switch is a picture of the card's state; the card itself is the control. The knob moves, so
   on and off differ by shape and position, not colour alone. */
.switch {
  position: relative;
  flex: none;
  width: 30px;
  height: 18px;
  background: #e5e7eb;
  border: 1.5px solid #6b7280;
  border-radius: 999px;
}

.switch__knob {
  position: absolute;
  top: 2px;
  inset-inline-start: 2px;
  width: 11px;
  height: 11px;
  background: #6b7280;
  border-radius: 50%;
}

.profile-card[aria-checked="true"] .switch {
  background: var(--pulxon-accent, #1f4bff);
  border-color: var(--pulxon-accent, #1f4bff);
}

.profile-card[aria-checked="true"] .switch__knob {
  inset-inline-start: auto;
  inset-inline-end: 2px;
  background: var(--pulxon-on-accent, #ffffff);
}

/* Font size: − 100% +. */
.stepper {
  margin-bottom: 10px;
  padding: 10px;
  background: #f7f8fb;
  border: 1px solid #e3e6ee;
  border-radius: 10px;
}

.stepper__label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 0.8125em;
  font-weight: 600;
}

.stepper__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.stepper__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  color: #111111;
  background: #e7eaf1;
  border: 0;
  border-radius: 50%;
  cursor: pointer;
}

.stepper__button[aria-disabled="true"] {
  opacity: 0.4;
  cursor: default;
}

.stepper__value {
  font-size: 0.9375em;
  font-weight: 700;
}

/* Tiles: three a row, as in Sienna. */
.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.tile {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-width: 0;
  min-height: 5.25em;
  padding: 10px 4px 8px;
  font: inherit;
  font-size: 0.75em;
  line-height: 1.2;
  text-align: center;
  /* Long words ("Encabezados") wrap inside a narrow tile instead of overflowing it. */
  overflow-wrap: anywhere;
  hyphens: auto;
  color: #111111;
  background: #ffffff;
  border: 1px solid #e3e6ee;
  border-radius: 10px;
  cursor: pointer;
}

.tile[aria-pressed="true"] {
  padding: 9px 3px 7px;
  color: #111111;
  background: #eef2ff;
  border: 2px solid var(--pulxon-accent, #1f4bff);
}

/* A shape, not just a colour: the check tells a colour-blind or greyscale reader the tile is on. */
.tile[aria-pressed="true"]::after {
  position: absolute;
  top: 3px;
  inset-inline-end: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  color: var(--pulxon-on-accent, #ffffff);
  font-size: 10px;
  line-height: 1;
  background: var(--pulxon-accent, #1f4bff);
  border-radius: 50%;
  /* The literal glyph, not a CSS hex escape: the escape is fragile through minification. */
  content: "✓";
}

/* The icon is decorative and inherits the tile's color, so it flips with the pressed state and
   needs no palette of its own. Fixed size keeps every tile's label on the same baseline. */
.tile__icon {
  flex: none;
  width: 22px;
  height: 22px;
}

.tile__dots { display: flex; justify-content: center; gap: 3px; }
.dot { width: 6px; height: 6px; border: 1px solid currentColor; border-radius: 50%; }
.dot--on { background: currentColor; }

.reset {
  width: 100%;
  min-height: 42px;
  margin-top: 12px;
  padding: 8px 16px;
  font: inherit;
  font-size: 0.875em;
  font-weight: 700;
  color: var(--pulxon-on-accent, #ffffff);
  background: var(--pulxon-accent, #1f4bff);
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
}

/* The footer stays under the scrolling body, like Sienna's: the accessibility statement. */
.panel__footer {
  flex: none;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  font-size: 0.8125em;
  background: #ffffff;
  border-top: 1px solid #e3e6ee;
}

/* Underlined, not colour alone (WCAG 1.4.1); padded to a 24px target. */
.panel__footer a {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 2px 4px;
  color: #111111;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
  border-radius: 6px;
}
.panel__footer a:hover { text-decoration-thickness: 2px; }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

/* Always shown beside the voice-navigation and dictionary tiles, never behind a disclosure — it is
   the whole reason those features are allowed to send anything anywhere. */
.feature-note {
  margin: 8px 0 0;
  font-size: 0.8125em;
}

/* The phrases a visitor can say, right under the voice note so turning the tile on tells them what
   to do next instead of leaving them guessing. */
.voice-commands {
  margin: 4px 0 0;
  padding-inline-start: 20px;
  font-size: 0.8125em;
}

.back {
  min-height: 44px;
  padding: 8px 12px;
  font: inherit;
  color: inherit;
  background: #ffffff;
  border: 1px solid #767676;
  border-radius: 8px;
  cursor: pointer;
}

.tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
}

.tab {
  flex: 1;
  min-height: 44px;
  padding: 8px;
  font: inherit;
  color: #111111;
  background: #ffffff;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
}

.tab[aria-selected="true"] {
  color: var(--pulxon-on-accent, #ffffff);
  background: var(--pulxon-accent, #1f4bff);
  border-color: #111111;
}

.structure__list {
  display: grid;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.structure__item {
  display: flex;
  gap: 8px;
  align-items: baseline;
  width: 100%;
  min-height: 44px;
  padding: 8px 12px;
  padding-inline-start: calc(12px + var(--pulxon-indent, 0px));
  font: inherit;
  text-align: start;
  color: #111111;
  background: #ffffff;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
}

.structure__detail { font-weight: 700; }

@media (max-width: 640px) {
  :host([data-hide-mobile]) .launcher { display: none; }

  [class*='launcher--m-'] { top: auto; right: auto; bottom: auto; left: auto; transform: none; }
  .launcher--m-top-left { top: var(--pulxon-oy); left: var(--pulxon-ox); }
  .launcher--m-top-center { top: var(--pulxon-oy); left: 50%; transform: translateX(-50%); }
  .launcher--m-top-right { top: var(--pulxon-oy); right: var(--pulxon-ox); }
  .launcher--m-center-left { top: 50%; left: var(--pulxon-ox); transform: translateY(-50%); }
  .launcher--m-center-right { top: 50%; right: var(--pulxon-ox); transform: translateY(-50%); }
  .launcher--m-bottom-left { bottom: var(--pulxon-oy); left: var(--pulxon-ox); }
  .launcher--m-bottom-center { bottom: var(--pulxon-oy); left: 50%; transform: translateX(-50%); }
  .launcher--m-bottom-right { bottom: var(--pulxon-oy); right: var(--pulxon-ox); }
}

@media print {
  .launcher,
  .panel { display: none; }
}

@media (prefers-reduced-motion: no-preference) {
  .tile { transition: background-color 0.15s ease; }
}
